import BonusCampaignList from './BonusCampaignList';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, X, Info, Clock, CalendarDays } from 'lucide-react';
import { SkeletonPage } from '../ui/kit';
import createBonusUI from '@voogo/bonus-ui';
import PageHeader from '../components/PageHeader';
import { nurDatum, uhrzeit } from '../lib/zeit';
const { LegacyBonusList } = createBonusUI(React);

const GREEN = '#9C27B0';

// BONUS-QUALIFIKATION-001: Wochen-Boni. Start ist immer der nächste Montag 04:00 (der Server bestimmt
// ihn); der Fortschritt beginnt jede Woche neu. Kein Zeitfenster, kein Pausieren.
const RECURRENCE_OPTIONS = [
  { v: 'weekly', t: 'Jede Woche' },
  { v: 'once', t: 'Einmalig – nur nächste Woche' },
];

export const startHint = (week) => {
  if (week?.next_week_start) {
    const tag = nurDatum(week.next_week_start).slice(0, 6);
    if (tag && tag !== '—') return `Startet Montag, ${tag}, ${uhrzeit(week.next_week_start)} Uhr`;
  }
  return 'Startet am nächsten Montag, 04:00 Uhr';
};

export const rulesText = (rules) => {
  const ok = rules && [rules.min_completed_rides, rules.min_acceptance_pct, rules.max_cancel_pct].every(Number.isFinite);
  if (!ok) return 'Wer den Bonus bekommt, entscheidet VOOGO nach festen Regeln (Fahrten in der Vorwoche, Annahmequote, Stornoquote).';
  return `Wer den Bonus bekommt, entscheidet VOOGO nach festen Regeln (mind. ${rules.min_completed_rides} Fahrten in der Vorwoche, Annahmequote ab ${rules.min_acceptance_pct} %, Stornoquote unter ${rules.max_cancel_pct} %).`;
};


const emptyForm = {
  title: '', per_ride_amount: '', completion_bonus: '', target_rides: '',
  scope: 'all', driver_ids: [],
  recurrence: 'weekly',
};

export default function Promotions() {
  const [bonuses, setBonuses] = useState([]);
  const [week, setWeek] = useState(null);
  const [rules, setRules] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    api.get('/partners/bonuses').then(r => {
      setBonuses(r.data.bonuses || []);
      setWeek(r.data.week || null);
      setRules(r.data.qualification_rules || null);
      setLoading(false);
    }).catch(() => {setMsg('Wochenboni konnten nicht geladen werden. Bitte neu laden.');setLoading(false);});
    api.get('/partners/drivers').then(r => setDrivers(r.data.drivers || r.data || [])).catch(() => setMsg('Fahrer konnten nicht geladen werden. Bitte neu laden.'));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDriver = (id) => setForm(f => ({ ...f, driver_ids: f.driver_ids.includes(id) ? f.driver_ids.filter(x => x !== id) : [...f.driver_ids, id] }));

  const submit = async () => {
    const perRide = parseFloat(form.per_ride_amount) || 0;
    const completion = parseFloat(form.completion_bonus) || 0;
    const target = parseInt(form.target_rides, 10) || 0;
    if (perRide <= 0 && completion <= 0) { setMsg('Mindestens Pro-Fahrt-Betrag oder Abschluss-Bonus angeben.'); return; }
    if (completion > 0 && target <= 0) { setMsg('Abschluss-Bonus braucht eine Ziel-Fahrtenzahl.'); return; }
    if (form.scope === 'selected' && form.driver_ids.length === 0) { setMsg('Bitte mindestens einen Fahrer auswählen.'); return; }
    setBusy(true); setMsg('');
    try {
      await api.post('/partners/bonuses', {
        title: form.title || (target > 0 ? `Quest: ${target} Fahrten` : 'Pro-Fahrt-Bonus'),
        per_ride_amount: perRide, completion_bonus: completion, target_rides: target,
        scope: form.scope, driver_ids: form.scope === 'selected' ? form.driver_ids : [],
        recurrence: form.recurrence,
      });
      setShowForm(false); setForm(emptyForm); load();
    } catch (e) { setMsg(e.response?.data?.error || 'Konnte nicht gespeichert werden.'); }
    setBusy(false);
  };

  const lbl = { fontSize: 12, fontWeight: 700, color: '#4b5563', marginBottom: 6, display: 'block' };
  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' };
  const hint = { display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: '#4b5563', lineHeight: 1.5 };

  return (
    <div>
      <PageHeader
        title="Aktionen & Boni"
        description="Bonusaktionen und Wochenboni für deine Fahrer. Gutschriften sind deine Zahlungspflicht; eine Bankauszahlung wird hier nicht bestätigt."
        actions={
        <button onClick={() => { setShowForm(s => !s); setMsg(''); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: showForm ? '#f3f4f6' : '#000', color: showForm ? '#374151' : '#fff', border: 'none', borderRadius: 12, padding: '11px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {showForm ? <X size={17} /> : <Plus size={17} />} {showForm ? 'Abbrechen' : 'Wochenbonus erstellen'}
        </button>
        }
      />

      <BonusCampaignList />

      {showForm && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 22, marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Titel (optional)</label>
              <input style={inp} value={form.title} onChange={e => set('title', e.target.value)} placeholder="z. B. Wochen-Quest" />
            </div>
            <div>
              <label style={lbl}>Betrag pro Fahrt (€)</label>
              <input style={inp} type="number" min="0" step="0.5" value={form.per_ride_amount} onChange={e => set('per_ride_amount', e.target.value)} placeholder="z. B. 2" />
            </div>
            <div>
              <label style={lbl}>Abschluss-Bonus (€)</label>
              <input style={inp} type="number" min="0" step="1" value={form.completion_bonus} onChange={e => set('completion_bonus', e.target.value)} placeholder="z. B. 50" />
            </div>
            <div>
              <label style={lbl}>Ziel-Fahrten pro Woche (für Abschluss-Bonus)</label>
              <input style={inp} type="number" min="0" step="1" value={form.target_rides} onChange={e => set('target_rides', e.target.value)} placeholder="z. B. 20" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Wie oft?</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {RECURRENCE_OPTIONS.map(({ v, t }) => (
                  <button key={v} type="button" aria-pressed={form.recurrence === v} onClick={() => set('recurrence', v)}
                    style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${form.recurrence === v ? GREEN : '#d1d5db'}`, background: form.recurrence === v ? '#faf5fb' : '#fff', color: form.recurrence === v ? '#82468c' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t}</button>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={hint}><CalendarDays size={14} style={{ marginTop: 2, flexShrink: 0 }} /> <span data-testid="bonus-start-hint">{startHint(week)}</span></div>
                <div style={hint}><Clock size={14} style={{ marginTop: 2, flexShrink: 0 }} /> <span>Fortschritt beginnt jede Woche neu</span></div>
                <div style={hint}><Info size={14} style={{ marginTop: 2, flexShrink: 0 }} /> <span data-testid="bonus-rules">{rulesText(rules)}</span></div>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Für wen?</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: form.scope === 'selected' ? 12 : 0 }}>
                {[{ v: 'all', t: 'Alle Fahrer' }, { v: 'selected', t: 'Ausgewählte' }].map(({ v, t }) => (
                  <button key={v} type="button" onClick={() => set('scope', v)}
                    style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${form.scope === v ? GREEN : '#d1d5db'}`, background: form.scope === v ? '#faf5fb' : '#fff', color: form.scope === v ? '#82468c' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t}</button>
                ))}
              </div>
              {form.scope === 'selected' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {drivers.map(d => (
                    <button key={d.id} type="button" onClick={() => toggleDriver(d.id)}
                      style={{ padding: '7px 12px', borderRadius: 20, border: `1px solid ${form.driver_ids.includes(d.id) ? GREEN : '#d1d5db'}`, background: form.driver_ids.includes(d.id) ? '#f3e5f5' : '#fff', color: form.driver_ids.includes(d.id) ? '#82468c' : '#374151', fontSize: 13, cursor: 'pointer' }}>
                      {d.name || d.driver_name || 'Fahrer'}
                    </button>
                  ))}
                  {drivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine Fahrer gefunden.</span>}
                </div>
              )}
            </div>
          </div>
          {msg && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 14 }}>{msg}</div>}
          <button onClick={submit} disabled={busy}
            style={{ marginTop: 18, background: GREEN, color: '#fff', border: 'none', borderRadius: 12, padding: '12px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Speichern…' : 'Bonus anlegen'}
          </button>
        </div>
      )}

      {msg && !showForm && <p role="alert">{msg} <button onClick={load}>Neu laden</button></p>}
      {loading ? <SkeletonPage /> : <LegacyBonusList api={api} scope="partners" rows={bonuses} onChanged={load}/>}
    </div>
  );
}
